import React, { Fragment, useState } from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import classes from '@nerdwallet/base-styles/classes';
import baseStyles, { ClassValue } from '@nerdwallet/base-styles/react';
import {
  Impression,
  ImpressionContext,
} from '@nerdwallet/analytics/react/impression';

import { spacing } from '@nerdwallet/base-styles/variables';
import StructuredContent from '@nerdwallet/structured-content';
import {
  AuthorByline,
  CenteredContainer,
  Stack,
  Box,
  Columns,
  Column,
  Text,
} from '@nerdwallet/currency';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';
import { useABTest } from '@nerdwallet/features';
import { SaveButton } from './save-button';

import ABConfig from '~/lib/ab-config';
import ContentPageHeader from '~/client/components/content-page-header/content-page-header';
import EditorialStandards from '~/client/components/editorial-standards';
import ArticleStructuredContent from '~/client/components/article-structured-content/article-structured-content';
import {
  augmentWithToc,
  constructTableOfContentsDocument,
  TocSchema,
} from '~/lib/structured-content';

import AuthorBox from '~/client/components/author-box/author-box';
import CitationList from '~/client/components/citation-list/citation-list';
import Methodology from '~/client/components/methodology/methodology';
import ConnectedOutdatedPageNotification from '~/client/components/outdated-page-notification/outdated-page-notification';
import HouseAd from '~/client/components/house-ad/house-ad';
import Hublinks from '../hubs-links/hubs-links';
import NextSteps from '~/client/components/next-steps/next-steps';
import RelatedLinks from '~/client/components/related-links/related-links';
import CCHeadToHead from '~/client/components/cc-head-to-head/cc-head-to-head';

import styles from './general-article.module.less';
import FadeContainer from '../fade-container';
import { ArticleTemplateProps } from '~/lib/types';
import { computeDisplayedDate } from '~/lib/article-utils';
import { getFormattedLendingDate } from '~/lib/lending-article-utils';
import { LendingFeaturedImage } from '../lending-featured-image/lending-featured-image';
import { SaveModal } from './save-modal';
import { trackElementInteracted } from '../../../lib/analytics.helper';

const dividerClasses: Array<ClassValue> = [
  'border-left-1',
  'padding-left-2',
  'margin-left-2',
  'border-neutral-light',
  'margin-bottom-2',
];

const GeneralArticle: React.FC<ArticleTemplateProps> = ({
  hasTableOfContents,
  tableOfContentsHeading,
  graphqlArticle,
  activeExperiments,
}) => {
  const { items: tocItems, augmentedDoc } = augmentWithToc(
    graphqlArticle.content
  );

  // Renders AB component for any active experiments
  const renderABContainers = () => {
    return activeExperiments.map((activeExperiment) => {
      const testConfig = _.find(ABConfig, {
        id: activeExperiment.experiment,
      });
      if (!testConfig) return null;
      return (
        <ABContainer experimentID={testConfig.id}>
          <Control />
          <Variant variantName={testConfig.variant} />
        </ABContainer>
      );
    });
  };

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const { ref: lazyHydrateRef } = useABTest({
    testName: 'cseo_sc_lazy_hydration',
  });

  const isInLazyExp = false;

  const toggleSaveModal = () => {
    setIsSaveModalOpen((prev) => !prev);
  };

  // TODO
  // uncomment the line below to enable back lazy hydration based on ab-experiment
  // variantName comes from the useABTest hook, add it back there when uncommenting
  // const isInLazyExp = variantName === 'is_lazy_hydrated';

  const NerdyTakeawayHighlightBox = (
    <div className={styles.takeawayBoxMargin}>
      <StructuredContent id="takeaways">
        {graphqlArticle.takeaways}
      </StructuredContent>
    </div>
  );

  const { hideFeaturedImage, showLastModifiedDate } = graphqlArticle;

  const HubsLinkMoreLikeThisSection = <Hublinks id={graphqlArticle.id} />;

  // Setting this up in a way to easily add other Lending specific verticals
  const isLendingVertical = ['Personal Loans'].includes(
    graphqlArticle?.primaryCategory?.name
  );

  const graphqlArticleCopy = { ...graphqlArticle };
  let lendingFormattedDisplayDate;
  let lendingFeaturedImageData;

  if (isLendingVertical) {
    lendingFormattedDisplayDate = getFormattedLendingDate(graphqlArticleCopy);

    if (!hideFeaturedImage && graphqlArticleCopy.featuredImage) {
      lendingFeaturedImageData = graphqlArticleCopy.featuredImage;
      // Need to set to undefined so image isn't rendered in currency component
      graphqlArticleCopy.featuredImage = undefined;
    }
  }

  return (
    <Box paddingTop="3">
      <CenteredContainer>
        {renderABContainers()}
        {!graphqlArticle.heroBannerDocument && (
          <React.Fragment>
            <ContentPageHeader
              article={graphqlArticleCopy}
              category={null}
              width={{ desktop: 8, mobile: 12 }}
              imageHasFullWidth
              hideEditorialStandards
              isLendingVertical={isLendingVertical}
            />
            {showLastModifiedDate && isLendingVertical && (
              <Box marginBottom="4" display="flex" flexDirection="row">
                <AuthorByline
                  authoringDate={
                    !isLendingVertical &&
                    computeDisplayedDate({
                      displayDate: graphqlArticle.dateTimeDisplayDate,
                      showLastModifiedDate: graphqlArticle.showLastModifiedDate,
                      hideDate: graphqlArticle.hideDate,
                      marketplaceEntities: null,
                    })
                  }
                  authors={graphqlArticle.authors.map((author) => ({
                    name: author.name,
                    url: author.link,
                  }))}
                />
                {lendingFormattedDisplayDate && (
                  <Box {...baseStyles(dividerClasses, 'nowrap')}>
                    <Text size="small" light>
                      {`${
                        showLastModifiedDate ? 'Last updated on ' : ''
                      }${lendingFormattedDisplayDate}`}
                    </Text>
                  </Box>
                )}
              </Box>
            )}
            {lendingFeaturedImageData && (
              <Columns>
                <Column width={{ desktop: 8, mobile: 12 }}>
                  <Box marginBottom={3}>
                    <LendingFeaturedImage data={lendingFeaturedImageData} />
                  </Box>
                </Column>
              </Columns>
            )}
            <Impression
              entityName="article_save_article_top_successful_display"
              eventType="element_impression"
            >
              <ImpressionContext.Consumer>
                {(impressionContext) => (
                  <SaveButton
                    onClick={() =>
                      trackElementInteracted({
                        entityName: 'article_save_article_top_user_click',
                        action: 'select',
                        interactionType: 'press',
                        impressionId: impressionContext?.impressionId,
                        onClick: toggleSaveModal,
                      })
                    }
                  />
                )}
              </ImpressionContext.Consumer>
            </Impression>
          </React.Fragment>
        )}
        <Box marginTop="2" />
        <Stack spacing={4}>
          <Columns>
            <Column width={{ desktop: 8, mobile: 12 }}>
              {graphqlArticle.heroBannerDocument &&
                graphqlArticle.showAuthorByline && (
                  <Box marginBottom="4">
                    {(!isLendingVertical || !showLastModifiedDate) && (
                      <Fragment>
                        <AuthorByline
                          authoringDate={computeDisplayedDate({
                            displayDate: graphqlArticle.dateTimeDisplayDate,
                            showLastModifiedDate:
                              graphqlArticle.showLastModifiedDate,
                            hideDate: graphqlArticle.hideDate,
                            marketplaceEntities: null,
                          })}
                          authors={graphqlArticle.authors.map((author) => ({
                            name: author.name,
                            url: author.link,
                          }))}
                        />
                        {lendingFormattedDisplayDate && (
                          <Box marginTop={2}>
                            <Text size="small" light>
                              {`Last updated on ${lendingFormattedDisplayDate}`}
                            </Text>
                          </Box>
                        )}
                      </Fragment>
                    )}
                    {showLastModifiedDate && isLendingVertical && (
                      <Box marginBottom="4" display="flex" flexDirection="row">
                        <AuthorByline
                          authors={graphqlArticle.authors.map((author) => ({
                            name: author.name,
                            url: author.link,
                          }))}
                        />
                        {lendingFormattedDisplayDate && (
                          <Box {...baseStyles(dividerClasses, 'nowrap')}>
                            <Text size="small" light>
                              {`${
                                showLastModifiedDate ? 'Last updated on ' : ''
                              }${lendingFormattedDisplayDate}`}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              {graphqlArticle.showEditorialDisclaimer && (
                <Box marginBottom="4">
                  <EditorialStandards article={graphqlArticle} />
                </Box>
              )}
              <ConnectedOutdatedPageNotification />
              <CCHeadToHead article={graphqlArticle} />
              <Stack spacing={4}>
                {hasTableOfContents && !!tocItems.length && (
                  <div className={classNames(styles['toc-wide-variant'])}>
                    <div className={styles.hideNotDesktop}>
                      <StructuredContent>
                        {constructTableOfContentsDocument({
                          items: tocItems,
                          hasJumpCTA: true,
                          showMore: true,
                          title: tableOfContentsHeading,
                        })}
                      </StructuredContent>
                      {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                      {graphqlArticle.hubLinks && HubsLinkMoreLikeThisSection}
                    </div>
                    <div className={styles.hideDesktop}>
                      <StructuredContent>
                        {constructTableOfContentsDocument({
                          items: tocItems,
                          hasJumpCTA: true,
                          title: tableOfContentsHeading,
                        })}
                      </StructuredContent>
                      {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                      {graphqlArticle.hubLinks && HubsLinkMoreLikeThisSection}
                    </div>
                  </div>
                )}
                <main>
                  <div
                    ref={lazyHydrateRef}
                    className={classes([
                      'margin-bottom-2',
                      'margin-bottom-3--wide',
                    ])}
                  >
                    {hasTableOfContents ? (
                      <ArticleStructuredContent
                        lazy={isInLazyExp}
                        schema={TocSchema}
                      >
                        {augmentedDoc}
                      </ArticleStructuredContent>
                    ) : (
                      <React.Fragment>
                        {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                        {graphqlArticle.hubLinks && HubsLinkMoreLikeThisSection}
                        <ArticleStructuredContent lazy={isInLazyExp}>
                          {graphqlArticle.content}
                        </ArticleStructuredContent>
                      </React.Fragment>
                    )}
                  </div>
                  <CitationList />
                  <AuthorBox article={graphqlArticle} />
                  <Impression
                    entityName="article_save_article_bottom_successful_display"
                    eventType="element_impression"
                  >
                    <ImpressionContext.Consumer>
                      {(impressionContext) => (
                        <SaveButton
                          onClick={() =>
                            trackElementInteracted({
                              entityName:
                                'article_save_article_bottom_user_click',
                              action: 'select',
                              interactionType: 'press',
                              impressionId: impressionContext?.impressionId,
                              onClick: toggleSaveModal,
                            })
                          }
                        />
                      )}
                    </ImpressionContext.Consumer>
                  </Impression>
                  <Methodology article={graphqlArticle} />
                </main>
              </Stack>
            </Column>
            {(RelatedLinks.shouldShow(graphqlArticle) ||
              graphqlArticle.sidebarDocument) && (
              <Column width={{ desktop: 4 }}>
                <Box
                  display={{ desktop: 'block', mobile: 'none' }}
                  position="relative"
                  height="100"
                  paddingLeft="3-half"
                  style={{ paddingTop: '14.5em' }}
                >
                  <FadeContainer offset={spacing['2']} fadeDistance={250}>
                    {graphqlArticle.sidebarDocument && (
                      <StructuredContent id="sidebarDocument">
                        {graphqlArticle.sidebarDocument}
                      </StructuredContent>
                    )}
                    {RelatedLinks.shouldShow(graphqlArticle) && (
                      <RelatedLinks article={graphqlArticle} maxItems={4} />
                    )}
                  </FadeContainer>
                </Box>
              </Column>
            )}
          </Columns>
          {graphqlArticle.bottomDocument && (
            <StructuredContent id="bottomDocument">
              {graphqlArticle.bottomDocument}
            </StructuredContent>
          )}
          {NextSteps.shouldShow(graphqlArticle) && (
            <NextSteps article={graphqlArticle} />
          )}
          <section className={styles.bottom}>
            {HouseAd.shouldShow(graphqlArticle) && (
              <Box marginBottom={4}>
                <HouseAd id={graphqlArticle.houseAd?.id} />
              </Box>
            )}
          </section>
        </Stack>
      </CenteredContainer>
      {graphqlArticle.bottomStickyDocument && (
        <div className={styles.stickyBottomBanner}>
          <StructuredContent>
            {graphqlArticle.bottomStickyDocument}
          </StructuredContent>
        </div>
      )}
      <SaveModal
        isOpen={isSaveModalOpen}
        onClose={toggleSaveModal}
        article={graphqlArticle}
      />
    </Box>
  );
};

GeneralArticle.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  activeExperiments: [],
};

export default GeneralArticle;
